import type { CheckResult } from '@domaintriage/shared';
import { DEFAULT_TLDS } from '@domaintriage/shared';

// ---------------------------------------------------------------------------
// Phase
// ---------------------------------------------------------------------------

export type Phase = 'input' | 'checking' | 'results';

// ---------------------------------------------------------------------------
// AppState
// ---------------------------------------------------------------------------

export interface AppState {
  /** Current UX phase. */
  phase: Phase;

  /** Raw text entered by the user (one domain / base-name per line). */
  inputText: string;

  /** TLDs to append in Mode B. */
  tlds: string[];

  /** Mode A = full domains as-entered; Mode B = base names x selected TLDs. */
  mode: 'A' | 'B';

  /** Results returned from the latest check. */
  results: CheckResult[];

  /** Whether the API indicated results were truncated. */
  truncated: boolean;

  /** How many domains were dropped due to truncation. */
  truncatedCount: number;

  /** Domain selected for the drill-in detail view. */
  selectedDomain: string | null;

  /** Whether the shortlist panel is visible. */
  showShortlist: boolean;

  /** Whether the privacy / about modal is visible. */
  showPrivacy: boolean;

  /** User-facing error message from the last operation, if any. */
  error: string | null;
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

export const initialState: AppState = {
  phase: 'input',
  inputText: '',
  tlds: [...DEFAULT_TLDS],
  mode: 'A',
  results: [],
  truncated: false,
  truncatedCount: 0,
  selectedDomain: null,
  showShortlist: false,
  showPrivacy: false,
  error: null,
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type AppAction =
  | { type: 'SET_INPUT_TEXT'; payload: string }
  | { type: 'SET_MODE'; payload: 'A' | 'B' }
  | { type: 'SET_TLDS'; payload: string[] }
  | { type: 'START_CHECK' }
  | {
      type: 'CHECK_SUCCESS';
      payload: {
        results: CheckResult[];
        truncated: boolean;
        truncatedCount: number;
      };
    }
  | { type: 'CHECK_ERROR'; payload: string }
  | { type: 'SELECT_DOMAIN'; payload: string | null }
  | { type: 'TOGGLE_SHORTLIST_PANEL' }
  | { type: 'TOGGLE_PRIVACY' }
  | { type: 'RESET' };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export function reducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_INPUT_TEXT':
      return { ...state, inputText: action.payload, error: null };

    case 'SET_MODE':
      return { ...state, mode: action.payload };

    case 'SET_TLDS':
      return { ...state, tlds: action.payload };

    case 'START_CHECK':
      return {
        ...state,
        phase: 'checking',
        results: [],
        truncated: false,
        truncatedCount: 0,
        error: null,
        selectedDomain: null,
      };

    case 'CHECK_SUCCESS':
      return {
        ...state,
        phase: 'results',
        results: action.payload.results,
        truncated: action.payload.truncated,
        truncatedCount: action.payload.truncatedCount,
        error: null,
      };

    case 'CHECK_ERROR':
      return {
        ...state,
        phase: 'input',
        error: action.payload,
      };

    case 'SELECT_DOMAIN':
      return { ...state, selectedDomain: action.payload };

    case 'TOGGLE_SHORTLIST_PANEL':
      return { ...state, showShortlist: !state.showShortlist };

    case 'TOGGLE_PRIVACY':
      return { ...state, showPrivacy: !state.showPrivacy };

    case 'RESET':
      return { ...initialState };

    default:
      return state;
  }
}
